import { isPlatformBrowser, Location } from "@angular/common";
import { Inject, Injectable, Injector, OnDestroy, PLATFORM_ID } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { doc, Firestore, getDoc } from "@angular/fire/firestore";
import { ActivatedRouteSnapshot, Data, NavigationStart, Resolve, Router, RouterStateSnapshot } from "@angular/router";
import { AuthPartialRegService } from "@core/AuthServices/authPartialReg/auth-partial-reg.service";
import { LoadingService } from "@core/loading.service";
import { UtilityService } from "@core/utility.service";
import { AuthService } from "@core/auth.service";
import { BehaviorSubject, firstValueFrom, from, Subscription } from "rxjs";
import { timeout } from "rxjs/operators";
import { awaitRegistrationOffer } from "./registration-offer-response";
import { registrationDefaults } from "../pages/sales/partial-registration/registration-defaults";
import { GTMSegmentService } from "./gtm-segment.service";
import { PaymentLogService } from "./payment-log.service";
import { PaymentService } from "./payment.service";

declare let analytics;
@Injectable({
  providedIn: "root",
})
export class LoginDataResolverService extends PaymentLogService implements Resolve<any>, OnDestroy {
  prosub_id;
  mod;
  prosub: any;
  userProduct$ = new BehaviorSubject<any>({});
  vc: any;
  affiliateId: any;
  campaignId: any;
  insertCustomerPurchaseRes: any;
  /**
   * Upper bound on the last-resort Firestore read below.
   */
  private static readonly PROSUB_READ_TIMEOUT_MS = 5000;
  private defaultProsubId = "3215";
  private prosubIdParamKey = "prosub_id";
  private undefinedString = "undefined";
  private nullString = "null";
  readonly partialRegistrationOffer$ = new BehaviorSubject<{ status: "loading" | "ready" | "error"; data?: Data; error?: string }>({ status: "loading" });
  private offerRequestId = 0;
  private readonly navigationSubscription: Subscription;
  constructor(
    private router: Router,
    protected firestore: Firestore,
    private loadingService: LoadingService,
    private location: Location,
    private injector: Injector,
    private paymentService: PaymentService,
    private utilityService: UtilityService,
    private afAuth: AngularFireAuth,
    private authPartialRegService: AuthPartialRegService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    super(afAuth, firestore);
    // A slow response must not start checkout after the visitor leaves the route.
    this.navigationSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart &&
          !/^\/partial-registration\/?(?:[?#]|$)/.test(event.url)) ++this.offerRequestId;
    });
  }

  ngOnDestroy(): void {
    ++this.offerRequestId;
    this.navigationSubscription.unsubscribe();
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (route.routeConfig?.path === "partial-registration") {
      this.startPartialRegistrationOffer(route, state);
      return { pending: true };
    }
    this.loadingService.present(20000);
    return this.resolveData(route, false);
  }

  startPartialRegistrationOffer(route: ActivatedRouteSnapshot, _state?: RouterStateSnapshot,
    locale?: { language: string; currency: string }): void {
    const requestId = ++this.offerRequestId;
    const browserLanguages = isPlatformBrowser(this.platformId)
      ? (navigator.languages?.length ? navigator.languages : [navigator.language]) : [];
    const selectedLocale = locale || registrationDefaults(route.queryParams, browserLanguages);
    this.partialRegistrationOffer$.next({ status: "loading" });
    void this.resolveData(route, true, requestId, selectedLocale).catch(error => {
      if (requestId === this.offerRequestId) {
        this.partialRegistrationOffer$.next({ status: "error", error: "Unable to check this offer. Please retry." });
      }
    });
  }

  private async resolveData(route: ActivatedRouteSnapshot, backgroundForm: boolean, requestId?: number,
    locale?: { language: string; currency: string }): Promise<Data> {
    try {
      if (isPlatformBrowser(this.platformId) && typeof localStorage !== "undefined") {
        localStorage.removeItem("minutes");
        localStorage.removeItem("c_id");
        localStorage.removeItem("initialProsub");
        localStorage.removeItem("paymentLog");
      }
    } catch (error) {
      // Storage can be unavailable in private browsing; offer lookup must still run.
    }
    var queryParams: Data = {};
    if (route.queryParams != null && route.queryParams != undefined) {
      queryParams = JSON.parse(JSON.stringify(route.queryParams));
    }
    Object.keys(queryParams).filter(key => key.toLowerCase() === this.prosubIdParamKey)
      .forEach(key => delete queryParams[key]);
    var queryParamsStr = Object.keys(queryParams)
      .map((key) => key + "=" + queryParams[key])
      .join("&");
    queryParamsStr = queryParamsStr ? "&" + queryParamsStr : "";

    const params = route.queryParams;
    const prosubIdKey = Object.keys(params).find(key => key.toLowerCase() === this.prosubIdParamKey);
    const prosub_id =
      prosubIdKey &&
        params[prosubIdKey] &&
        params[prosubIdKey].toLowerCase() !== this.undefinedString &&
        params[prosubIdKey].toLowerCase() !== this.nullString
        ? params[prosubIdKey]
        : this.defaultProsubId;
    const queryValue = (name: string) => {
      const key = Object.keys(params).find(candidate => candidate.toLowerCase() === name);
      return key ? params[key] : undefined;
    };
    this.affiliateId = queryValue("affiliate_id");
    this.campaignId = queryValue("campaign_id");
    this.vc = route.queryParams.vc;
    let prosubDetails: Data = {};
    let data: Data = {};
    let redirectURI: string | null = null;
    try {
      data = await this.getData(prosub_id, this.affiliateId, this.campaignId,
        backgroundForm ? "route" : "full", locale);
      if (requestId && requestId !== this.offerRequestId) return {};
      if ((this.affiliateId || this.campaignId) && data?.affiliateMappingMatched !== true) {
        throw new Error("affiliate_mapping_unavailable");
      }
      if (data?.error || !/^[1-9]\d*$/.test(String(data?.prosubDetail?.prosub_id || "")) ||
          !Number.isInteger(Number(data?.prosubDetail?.offer_id)) || Number(data?.prosubDetail?.offer_id) <= 0 ||
          !Number.isFinite(Number(data?.prosubDetail?.price)) || Number(data?.prosubDetail?.price) < 0) {
        throw new Error(String(data?.error || "Offer configuration unavailable"));
      }
      if (!data.modData) {
        // partialregv2 intentionally omits form modules for authenticated members.
        // Accept only the configured internal destination, with a real member
        // session, before restoring the existing checkout initialization path.
        redirectURI = this.getMemberDestination(data);
        const user = redirectURI
          ? await firstValueFrom(from(this.afAuth.currentUser).pipe(timeout(5000)))
          : null;
        if (requestId && requestId !== this.offerRequestId) return {};
        if (!redirectURI || !user?.uid || user.isAnonymous) {
          throw new Error("Offer routing unavailable");
        }
      }
      this.prosub_id = data.prosubDetail.prosub_id;
    } catch (error) {
      if (backgroundForm) {
        if (requestId === this.offerRequestId) {
          this.partialRegistrationOffer$.next({ status: "error", error:
            String(error?.message || error).includes("affiliate_mapping_unavailable")
              ? "This referral offer is unavailable. Please check the link or retry."
              : "Unable to check this offer. Please retry." });
        }
      } else {
        this.loadingService.dismiss();
        this.router.navigateByUrl("/product-not-available");
      }
      return { error: String(error?.message || error) };
    }
    prosubDetails = data?.prosubDetail;
    const gtmService = this.injector.get(GTMSegmentService);
    gtmService.prosubDetails = data?.prosubDetail;
    this.userProduct$.next(this.formPaymentDataForTopUp(prosubDetails));
    localStorage.setItem("jackpotPrize", JSON.stringify(this.userProduct$?.value));
    if (!backgroundForm) this.loadingService.dismiss();
    if (data && data.prosubDetail && data.prosubDetail.optional_page) {
      localStorage.setItem("flowPath", data.prosubDetail.optional_page);
    }
    if (this.prosub_id) {
      localStorage.setItem("prosub_id", this.prosub_id);
      localStorage.setItem("initialProsub", this.prosub_id);
    }
    if (data?.modData) {
      if (backgroundForm) {
        this.partialRegistrationOffer$.next({ status: "ready", data });
      } else {
        this.redirectToPartialRegPage(this.prosub_id, queryParamsStr);
      }
      return data;
    }

    // A validated member response follows the same destination and checkout
    // contract whether it arrived through /interim or the form-first route.
    this.insertCustomerPurchaseAndCreateCheckout();
    if (data.prosubDetail.optional_page == "free-reg-step4d") {
      this.loadingService.dismiss();
      this.router.navigate([redirectURI], {
        //queryParams: { prosub_id: this.prosub_id, vc: this.vc, "myTest": "testing" },
        queryParams: { prosub_id: this.prosub_id, ...queryParams },
        state: data,
      });
    } else {
      this.loadingService.present(20000);
      this.router.navigate([redirectURI], {
        //queryParams: { prosub_id: this.prosub_id, "key": "value" },
        queryParams: { prosub_id: this.prosub_id, ...queryParams },
        state: data,
      });
    }
    return data;
  }

  private getMemberDestination(data: Data): string | null {
    const destination = data?.redirectURI;
    const configured = data?.prosubDetail?.optional_page;
    if (typeof destination !== "string" || typeof configured !== "string" ||
        !/^\/?[a-z][a-z0-9-]*(?:\/[a-z][a-z0-9-]*)*$/i.test(destination)) return null;
    const path = destination.replace(/^\//, "");
    if (path === "partial-registration" || path !== configured.replace(/^\//, "")) return null;
    return `/${path}`;
  }

  formPaymentDataForTopUp(offer: any) {
    const safeOffer = offer && typeof offer === "object" ? offer : {};
    const rawPrice = safeOffer.price;
    const numericPrice =
      typeof rawPrice === "number"
        ? rawPrice
        : typeof rawPrice === "string" && rawPrice.trim() !== ""
          ? Number(rawPrice)
          : NaN;
    return {
      prosub_id: safeOffer.prosub_id ?? "",
      phone: safeOffer.phone ?? "",
      // GTM Axon tags read jackpotPrize.prize — must be a finite number (LOTTOSOCIAL-PWA-1NBD).
      prize: Number.isFinite(numericPrice) ? numericPrice : 0,
      offers: safeOffer,
      checkout: true,
      draw: "",
      game: "",
      line: "",
      offer_id: safeOffer.offer_id ?? "",
      real_name: "",
      page: "store",
      TP1: this.prosub && this.prosub.params && this.prosub.params.TP1 ? this.prosub.params.TP1?.toLowerCase() : "",
      TP2: this.prosub && this.prosub.params && this.prosub.params.TP2 ? this.prosub.params.TP2?.toLowerCase() : "",
      TP3:
        this.prosub && this.prosub.params
          ? this.prosub.params.TP3
            ? this.prosub.params.TP3?.toLowerCase()
            : this.prosub.params[
              Object.keys(this.prosub.params)?.find((key) => key?.toLowerCase() === "tp3")
            ]?.toLowerCase() ?? ""
          : "",
      T_ID:
        this.prosub && this.prosub.params
          ? this.prosub.params.T_ID
            ? this.prosub.params.T_ID?.toLowerCase()
            : this.prosub.params[
              Object.keys(this.prosub.params)?.find((key) => key?.toLowerCase() === "t_id")
            ]?.toLowerCase() ?? ""
          : "",
      other:
        this.prosub && this.prosub.params && this.prosub.params.Other ? this.prosub.params.Other?.toLowerCase() : "",
      vc: this.vc,
    };
  }
  GTMPopupInitialization(msg: any, body?: any) {
    analytics.track(msg, {
      data: msg,
      ...(body != null ? { message: body } : {}),
    });
  }

  getData(prosub_id: string, affiliateId: any, campaignId: any, responseMode: "route" | "full" = "full",
    locale?: { language: string; currency: string }): Promise<Data> {
    const authService = this.injector.get(AuthPartialRegService);
    return awaitRegistrationOffer(authService.partial_Reg(prosub_id, affiliateId, campaignId, responseMode, locale));
  }

  insertCustomerPurchaseAndCreateCheckout(cp_request?: Data) {
    const cp_req = cp_request ?? {};
    localStorage.setItem("initialProsub", this.userProduct$.value?.prosub_id);
    this.callAPIForPaymentInit(cp_req);
  }
  private callAPIForPaymentInit(cp_request) {
    try {
      // Snapshot once so rawPrize and currency always come from the same emission.
      const product = this.userProduct$.value;
      const rawPrize = product?.prize;
      // Only accept number or string types; other types (boolean, array, object)
      // can coerce to misleading values via Number() and are treated as invalid.
      const prizeIsAcceptableType = typeof rawPrize === 'number' || typeof rawPrize === 'string';
      // For string values, require a canonical decimal format: digits optionally followed
      // by a decimal point and more digits. This intentionally rejects hex ("0x10"),
      // scientific ("1e3"), binary ("0b10"), partial-numeric ("10abc"), and leading-dot
      // (".99") formats — only clean decimal amounts are accepted for checkout.
      const isValidDecimalString = typeof rawPrize === 'string'
        && /^\d+(\.\d+)?$/.test(rawPrize.trim());
      // Compute the numeric value; assign NaN for any unacceptable input so the
      // guard below can handle all invalid cases uniformly.
      const parsedAmount = !prizeIsAcceptableType
        ? NaN
        : typeof rawPrize === 'number'
          ? rawPrize
          : isValidDecimalString ? Number(rawPrize.trim()) : NaN;
      // Negative amounts are invalid for checkout. Zero is intentionally allowed
      // here because the pre-auth branch below (prize == 0) handles it explicitly.
      if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
        // Log type and a safely truncated value — avoids large/unhelpful output for
        // unexpected object types while still giving enough context to diagnose the issue.
        const safeValue = prizeIsAcceptableType
          ? String(rawPrize).slice(0, 80)
          : `[${typeof rawPrize}]`;
        this.injector.get(AuthService).errorHandlerLogs(
          `[LoginDataResolverService] callAPIForPaymentInit: invalid or missing prize value (${safeValue})`
        );
        return;
      }
      const amount = parsedAmount.toFixed(2);
      const currency = product?.offers?.currency || "GBP";

      this.updateLogToLocal({
        co_request: { amount, currency },
        co_startDate: new Date(),
      });

      // Use the already-validated parsedAmount rather than the raw prize value
      // to avoid JavaScript loose-equality coercion on the original string/number.
      if (parsedAmount === 0) {
        cp_request["payment_type"] = "PA";
        cp_request["sub_status"] = "pre_auth"; // this is used so no ticket is assigned
      }
      const paymentReason = "offer";
      this.paymentService
        .getCheckoutId(
          amount,
          currency,
          cp_request,
          paymentReason
        )
        .subscribe((res) => {
          if (res.id) {
            localStorage.setItem("c_id", res?.id);
            this.paymentService.currentNdcId = res.id;
            const now = new Date().getMinutes();
            localStorage.setItem("minutes", now.toString());
            this.updateLogToLocal({ co_response: res, co_endDate: new Date() });
          }
        });
    } catch (error) {
      return error;
    }
  }

  private redirectToPartialRegPage(prosubId: string, queryParamsStr: string): void {
    setTimeout(() => {
      this.loadingService.dismiss();
      const newUrl = "/partial-registration?" + "prosub_id=" + prosubId + queryParamsStr;

      if (this.router.url.startsWith("/partial-registration")) {
        // If already on the partial-registration page, replace the state
        this.authPartialRegService.normalizeQueryParams();
      } else {
        // Otherwise, navigate to the partial-registration page
        this.router.navigateByUrl(newUrl);
      }
    }, 2000);
  }

  /**
  * Fetches the prosub details from firebase given a prosub ID.
  * @param prosub_id - The prosub ID to fetch details for.
  * @returns A Promise resolved with the prosub details.
  */
  private async getProsubDetails(prosub_id: string): Promise<any> {
    // Modular Firestore API: Reference the document path
    const prosubDetailsDocRef = doc(this.firestore, `/general_modules/prosub_id/${prosub_id}/details`);
    // Use `getDoc()` to fetch the document data, bounded so an unreachable
    // Firestore cannot hold the registration form back indefinitely.
    const documentSnapshot = await firstValueFrom(
      from(getDoc(prosubDetailsDocRef)).pipe(timeout(LoginDataResolverService.PROSUB_READ_TIMEOUT_MS))
    );
    return documentSnapshot.data(); // Extract and return document data
  }
}

